import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router';
import LoadingScreen from "../components/LoadingScreen/Component";
import React from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Router } from "@remix-run/router"
import ErrorPage from '../pages/Error/Component';
import AuthGuard from '../components/Guards/Auth/Component';
import GuestGuard from '../components/Guards/Guest/Component';
import { HelmetMetaInformation } from './CSP/Helmet';

const Loadable = (Component: any) => (props: JSX.IntrinsicAttributes & any) => {
    return (
        <Suspense fallback={<LoadingScreen />}>
            <Component {...props} />
        </Suspense>
    );
}

//#region Pages

const Home = Loadable(lazy(() => import("../pages/Home/Component")));
// const Login = Loadable(lazy(() => import("../pages/Login/Component")))

//#region Admin

const Area = Loadable(lazy(() => import("../pages/Admin/Area/Component")));
const Company = Loadable(lazy(() => import("../pages/Admin/Company/Component")));
const DeliberationPhrases = Loadable(lazy(() => import("../pages/Admin/DeliberationPhrases/Component")));
const User = Loadable(lazy(() => import("../pages/Admin/User/Component")));
const TemplateEmail = Loadable(lazy(() => import("../pages/Admin/TemplateEmail/Component")));

//#endregion

const Meetings = Loadable(lazy(() => import("../pages/Meetings/Component")));
const MeetingsList = Loadable(lazy(() => import("../pages/MeetingsList/Component")));

//#region Proposals

const ProposalAdmin = Loadable(lazy(() => import("../pages/Proposal/Admin/Component")));
const ProposalManager = Loadable(lazy(() => import("../pages/Proposal/Manager/Component")));
const ProposalByState = Loadable(lazy(() => import("../pages/Proposal/ByStatus/Component")));
const ProposalGuest = Loadable(lazy(() => import("../pages/Proposal/Admin/Component")));

//#endregion

const Agenda = Loadable(lazy(() => import("../pages/Agenda/Component")));

//#endregion

const MountRouteObject = ({ Route, Error, Page, index = false }: { Route: string, Page: JSX.Element, Error?: JSX.Element, index?: boolean }): RouteObject => {
    const result: RouteObject = {
        path: Route,
        element: Page,
        errorElement: Error || <ErrorPage />,
        hydrateFallbackElement: <HelmetMetaInformation />,
        index
    };
    return result;
}

const RoutesConfig: RouteObject[] = [
    MountRouteObject({ Route: "*", Page: <ErrorPage errorCode={404} /> }),

    MountRouteObject({ Route: "/", Page: <AuthGuard children={<Home />} />, index: true }),
    MountRouteObject({ Route: "/Home", Page: <AuthGuard children={<Home />} /> }),

    //#region Admin

    MountRouteObject({ Route: "/Admin/Area", Page: <AuthGuard children={<Area />} check={['Admin', 'Master']} /> }),
    MountRouteObject({ Route: "/Admin/Proposals", Page: <AuthGuard children={<ProposalAdmin />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Admin/DeliberationPhrase", Page: <AuthGuard children={<DeliberationPhrases />} check={['Admin', 'Master']} /> }),
    MountRouteObject({ Route: "/Admin/User", Page: <AuthGuard children={<User />} check={['Admin', 'Master']} /> }),
    MountRouteObject({ Route: "/Admin/TemplateEmail", Page: <AuthGuard children={<TemplateEmail />} check={['Admin']} /> }),
    MountRouteObject({ Route: "/Admin/Company", Page: <AuthGuard children={<Company />} check={['Admin', 'Master']} /> }),

    //#endregion

    MountRouteObject({ Route: "/Meetings", Page: <AuthGuard children={<Meetings />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/MeetingsList", Page: <AuthGuard children={<MeetingsList />} check={['Admin', 'Master']} /> }),

    //#region Proposals

    MountRouteObject({ Route: "/Guest/Proposals", Page: <AuthGuard children={<ProposalGuest />} check={['Guest']} /> }),
    MountRouteObject({ Route: "/Proposal/New", Page: <AuthGuard children={<ProposalManager />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Edit", Page: <AuthGuard children={<ProposalManager />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),

    //#endregion

    //#region Proposals By status

    MountRouteObject({ Route: "/Proposal/All", Page: <AuthGuard children={<ProposalByState status="All" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/AwaitingApprovalLeaderProposal", Page: <AuthGuard children={<ProposalByState status="AwaitingApprovalLeaderProposal" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/AwaitingSubmissionForMeeting", Page: <AuthGuard children={<ProposalByState status="AwaitingSubmissionForMeeting" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Deleted", Page: <AuthGuard children={<ProposalByState status="Deleted" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Deliberated", Page: <AuthGuard children={<ProposalByState status="Deliberated" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/DirectorReview", Page: <AuthGuard children={<ProposalByState status="DirectorReview" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Draft", Page: <AuthGuard children={<ProposalByState status="Draft" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/DraftAux", Page: <AuthGuard children={<ProposalByState status="DraftAux" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/InSchedule", Page: <AuthGuard children={<ProposalByState status="InSchedule" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/PendingForDeliberation", Page: <AuthGuard children={<ProposalByState status="PendingForDeliberation" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Returned", Page: <AuthGuard children={<ProposalByState status="Returned" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/Shared", Page: <AuthGuard children={<ProposalByState status="Shared" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),
    MountRouteObject({ Route: "/Proposal/WithdrawnSchedule", Page: <AuthGuard children={<ProposalByState status="WithdrawnSchedule" />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> }),

    //#endregion

    MountRouteObject({ Route: "/Agenda", Page: <AuthGuard children={<Agenda />} check={['Admin', 'Director', 'Guest', 'Presenter']} /> })
]
    .filter((x: RouteObject) => x && x.path && (x.Component || x.children || x.element));

export const GetBrowserRouter = (): Router => createBrowserRouter(RoutesConfig);

export default RoutesConfig;